import type { FC } from 'react';
import * as React from 'react';
import { Card, Stack, styled, useTheme } from '@mui/material';
import IconStripe from '~icons/bi/stripe';
import IconFileFill from '~icons/eva/file-fill';

import { TotalAnalytic } from './TotalAnalytic';
import { useTranslation } from 'react-i18next';
import {
  useCashListConfiguration,
  type CashList,
} from '@pflegenavi/frontend/api-nursing-home';
import { useCashListIcon } from '@pflegenavi/web-components';
import { CashListStorageType } from '@pflegenavi/shared/api';

interface NewAccountingOverviewTotalsProps {
  stripeInCent: number;
  receiptsInCent: number;
  totalPendingReversalsInCent: number;
  cashLists: CashList[];
  payoutSumInCent: number;
  serviceProviderPaymentSumInCent: number;
  coinListEnabled: boolean;
}

export const NewAccountingOverviewTotals: FC<
  NewAccountingOverviewTotalsProps
> = (props) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const cashListConfiguration = useCashListConfiguration();

  const {
    cashLists,
    serviceProviderPaymentSumInCent,
    payoutSumInCent,
    coinListEnabled,
  } = props;

  const bankAccountCashLists = cashLists
    .filter(
      (cashList) =>
        cashList && cashList.storageType === CashListStorageType.BankAccount
    )
    .map((cashList) => {
      let sumInCent = cashList.totalInCent;

      if (cashListConfiguration.payoutTargetCashListId === cashList.id) {
        sumInCent += payoutSumInCent;
      }
      if (
        cashListConfiguration.serviceProviderPaymentTargetCashListId ===
        cashList.id
      ) {
        sumInCent -= serviceProviderPaymentSumInCent;
      }

      return {
        ...cashList,
        totalInCent: sumInCent,
      };
    });

  const bankAccountAmountInCent = bankAccountCashLists.reduce(
    (acc, cashList) => acc + cashList.totalInCent,
    0
  );
  const cashAccountCashLists = cashLists.filter(
    (cashList) => cashList.storageType & CashListStorageType.Cash
  );

  const cashAccountAmountInCent = cashAccountCashLists.reduce(
    (acc, cashList) => acc + cashList.totalInCent,
    0
  );

  const stripeInCent = props.stripeInCent + props.totalPendingReversalsInCent;

  const totalInCent =
    stripeInCent +
    props.receiptsInCent +
    (coinListEnabled ? bankAccountAmountInCent : 0) +
    (coinListEnabled ? cashAccountAmountInCent : 0);

  const percentages: {
    [key: string]: number;
  } = {
    stripe: (stripeInCent / totalInCent) * 100,
    receipts: (props.receiptsInCent / totalInCent) * 100,
  };

  if (coinListEnabled) {
    for (const bankCashList of bankAccountCashLists) {
      percentages[bankCashList.name] =
        (bankCashList.totalInCent / totalInCent) * 100;
    }

    for (const cashCashList of cashAccountCashLists) {
      percentages[cashCashList.name] =
        (cashCashList.totalInCent / totalInCent) * 100;
    }
  }

  const totalItems = coinListEnabled
    ? bankAccountCashLists.length + cashAccountCashLists.length + 2
    : 2;

  return (
    <Stack
      direction="row"
      sx={{
        width: '100%',
        flexWrap: 'wrap',
        gap: totalItems > 4 ? 3 : 1.5,
        rowGap: totalItems > 4 ? 3 : 1.5,
      }}
    >
      <CardContainer totalItems={totalItems}>
        <TotalAnalytic
          sx={{
            width: 'fit-content',
            minWidth: 190,
            padding: { xs: 0 },
            justifyContent: 'flex-start',
          }}
          title="Stripe"
          percent={percentages.stripe}
          amountInCent={stripeInCent}
          Icon={IconStripe}
          color={theme.palette.info.main}
        />
      </CardContainer>

      {coinListEnabled &&
        bankAccountCashLists.map((cashList) => (
          <CashListCard
            key={cashList.id}
            cashList={cashList}
            totalItems={totalItems}
            percent={percentages[cashList.name]}
            color={theme.palette.warning.main}
          />
        ))}
      {coinListEnabled &&
        cashAccountCashLists.map((cashList) => (
          <CashListCard
            key={cashList.id}
            cashList={cashList}
            totalItems={totalItems}
            percent={percentages[cashList.name]}
            color={theme.palette.success.main}
          />
        ))}

      <CardContainer totalItems={totalItems}>
        <TotalAnalytic
          sx={{
            width: 'fit-content',
            minWidth: 190,
            padding: { xs: 0 },
            justifyContent: 'flex-start',
          }}
          title={t('receipts.main')}
          percent={percentages.receipts}
          amountInCent={props.receiptsInCent}
          Icon={IconFileFill}
          color={theme.palette.text.secondary}
        />
      </CardContainer>
    </Stack>
  );
};

const CardContainer = styled(Card)<{ totalItems: number }>(
  ({ totalItems }) => ({
    padding: totalItems > 4 ? 24 : 16,
    display: 'flex',
  })
);

interface CashListCardProps {
  cashList: CashList;
  totalItems: number;
  percent: number;
  color: string;
}

const CashListCard: FC<CashListCardProps> = ({
  cashList,
  totalItems,
  percent,
  color,
}) => {
  const Icon = useCashListIcon(cashList);
  return (
    <CardContainer totalItems={totalItems}>
      <TotalAnalytic
        sx={{
          width: 'fit-content',
          minWidth: 190,
          padding: { xs: 0 },
          justifyContent: 'flex-start',
        }}
        title={cashList.name}
        percent={percent}
        amountInCent={cashList.totalInCent}
        Icon={Icon}
        color={color}
      />
    </CardContainer>
  );
};
